// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163319 $
//  Purpose   : uix request decoding
//              decode 6-tupel: (dname, cname, classname, tabname, ctxid, id)
//              handle auth
// =============================================================================
// ---Third-party imports
import ExtendableError from 'es6-error';
// ---Framework imports
import { filter, merge, has, entries, find, each, get, fromPairs, mapItems, size } from 'ldash';
import log from 'uix/logging';
import { UnloadedDomainError } from 'uix/error';
import { Actions } from 'uix/actions';
import { decodeTypes, decodeType } from 'uix/utils';
import { cleanPathPrefix } from 'uix/deploy';
import { parseQuery } from './query';
import { DEFAULT_ROUTE_PATTERN, parseDefaultRoute } from './redux_router';
// ---Globals
export const routes = {
    [DEFAULT_ROUTE_PATTERN]: {
        title: 'from Routing'
    }
};
export class InvalidRouteError extends ExtendableError {
    constructor(params, message){
        super(message);
        this.params = params;
        this.code = 'E_INVALID_ROUTE';
    }
}
export const ensurePathPrefix = (pathPrefix, pathname)=>pathPrefix ? `/${pathPrefix}${pathname}` : pathname;
export const normalizeBeautifyPattern = (pattern)=>String(pattern || '').replace(/^\/+|\/+$/g, '');
const beautifyEntryFromValues = (routingBeautify, values, patternIndex)=>{
    let candidates = routingBeautify.map((entry)=>{
        let patternParts = normalizeBeautifyPattern(entry.pattern).split('/').filter(Boolean);
        if (!size(patternParts)) {
            return null;
        }
        let matched = patternParts.every((part, idx)=>values[patternIndex + idx] === part);
        if (!matched) {
            return null;
        }
        return {
            entry,
            patternParts
        };
    }).filter(Boolean);
    if (!size(candidates)) {
        return null;
    }
    return candidates.sort((a, b)=>b.patternParts.length - a.patternParts.length)[0];
};
const beautifyAllowsId = (entry, id)=>{
    let allowedIds = entry?.otids || [];
    if (!size(allowedIds)) {
        return true;
    }
    let sid = String(id);
    return !!find(allowedIds, (allowed)=>String(allowed) === sid);
};
const beautifyTargetParts = (entry)=>{
    let parts = (entry.target || '').split('/').filter(Boolean);
    let [classname, tabname] = parts;
    return {
        classname,
        tabname
    };
};
export const applyRoutingBeautifyParams = (params = {}, routingBeautify = [], domains = {}, pathPrefix = '')=>{
    let values = [
        params.p0,
        params.p1,
        params.p2,
        params.p3,
        params.p4,
        params.p5,
        params.p6
    ];
    let domainIndex = values[0] === pathPrefix ? 1 : 0;
    let hasDomainSegment = has(domains.name2prefix, values[domainIndex]) || has(domains.prefix2name, values[domainIndex]);
    let patternIndex = hasDomainSegment ? domainIndex + 1 : domainIndex;
    if (!values[patternIndex]) {
        return params;
    }
    let matched = beautifyEntryFromValues(routingBeautify, values, patternIndex);
    if (!matched) {
        return params;
    }
    let { entry, patternParts } = matched;
    let { classname, tabname } = beautifyTargetParts(entry);
    if (!classname || !tabname) {
        return params;
    }
    let tail = values.slice(patternIndex + patternParts.length);
    let [id, ...rest] = tail;
    id = id || entry.defaultId;
    if (!id) {
        return params;
    }
    if (!beautifyAllowsId(entry, id)) {
        return params;
    }
    let next = [
        ...values.slice(0, patternIndex),
        classname,
        tabname,
        id,
        ...rest
    ];
    return {
        p0: next[0],
        p1: next[1],
        p2: next[2],
        p3: next[3],
        p4: next[4],
        p5: next[5],
        p6: next[6]
    };
};
export let decodeUrl = (url)=>{
    // make absolute - do we really allow proto+fqdn here?
    if (url.slice(0, 4) !== 'http' && url[0] !== '/') {
        url = '/' + url;
    }
    let u = new URL(url, 'http://localhost');
    let pathname = u.pathname;
    let search = u.search;
    let params = parseDefaultRoute(pathname);
    let query = parseQuery(search);
    return {
        params,
        query,
        search,
        pathname
    };
};
export const decodeDomain = (params, domains, pathPrefix)=>{
    /* only decode domain from router params */ let { p0, p1 } = params;
    let p = p0 === pathPrefix ? p1 : p0;
    let dname = domains.rootDomain;
    if (has(domains.name2prefix, p)) {
        // first param is dname
        dname = p;
    } else if (has(domains.prefix2name, p)) {
        dname = domains.prefix2name[p];
    }
    return dname;
};
export const decodeRequest = (params, query, config)=>{
    params = applyRoutingBeautifyParams(params, config.routingBeautify, config.domains, get(config, 'deploy.pathPrefix', ''));
    // pathPrefix / domain / collection / class / tab / context / id
    let { p0, p1, p2, p3, p4, p5, p6 } = params || {} // XXX: can be undefined in SSR?
    ;
    // we are going to consume this list with pop()
    let candidates = [
        p6,
        p5,
        p4,
        p3,
        p2,
        p1,
        p0
    ];
    // fetch last without mutation
    let candidate = ()=>candidates.slice(-1)[0];
    let evalPathPrefix = ()=>{
        let c = candidate();
        if (config.deploy.pathPrefix === c) {
            candidates.pop();
        }
    };
    let evalDomain = ()=>{
        let c = candidate();
        let domains = config.domains;
        let dname = domains.rootDomain;
        if (has(domains.name2prefix, c)) {
            // first param is dname
            dname = candidates.pop();
        } else if (has(domains.prefix2name, c)) {
            dname = domains.prefix2name[candidates.pop()];
        }
        if (!has(config.templating, dname)) {
            throw new UnloadedDomainError(dname);
        }
        return dname;
    };
    let evalCollection = ()=>{
        let c = candidate();
        let cname = null;
        return cname;
    };
    let evalView = (dname)=>{
        let authNeeded = false;
        let c = candidate();
        let classname = null;
        let tabname = null;
        let tcfg = config.templating[dname].classes;
        let isDefault = {
            tabname: false,
            classname: false
        };
        // test classname
        if (!c) {
            let defaultClass = find(entries(tcfg), (e)=>e[1].default);
            if (defaultClass) {
                classname = defaultClass[0];
                isDefault.classname = true;
            }
        } else if (has(tcfg, c)) {
            classname = candidates.pop();
        }
        if (classname) {
            // test tabname
            c = candidate();
            let classcfg = tcfg[classname];
            if (classcfg.authNeeded) {
                authNeeded = true;
            }
            let tabs = classcfg.tabs;
            if (c && has(tabs, c)) {
                tabname = candidates.pop();
            } else {
                // allow default-tab-fallback only if one (id) candidate left (no contextid)
                let ctxid_available = candidates.filter((e)=>e).length > 1;
                if (!ctxid_available) {
                    // find default
                    let defaultTab = find(entries(tabs), (e)=>e[1].default);
                    if (defaultTab) {
                        tabname = defaultTab[0];
                        isDefault.tabname = true;
                    }
                }
            }
            if (tabname) {
                let tabcfg = tabs[tabname];
                if (classcfg.authNeeded || tabcfg && tabcfg.authNeeded) {
                    authNeeded = true;
                }
            }
        }
        return {
            classname,
            tabname,
            authNeeded,
            isDefault
        };
    };
    let evalIds = ()=>{
        let ctxid = null;
        let id = null;
        let c = candidate();
        if (c) {
            id = decodeType(candidates.pop());
        }
        // also check for context id
        if (id !== null) {
            c = candidate();
            if (c) {
                ctxid = id;
                id = decodeType(candidates.pop());
            }
        }
        return {
            ctxid,
            id
        };
    };
    // check if p0 is a custom pathPrefix and ignore
    evalPathPrefix();
    let dname = evalDomain();
    let cname = evalCollection();
    let { classname, tabname, authNeeded, isDefault } = evalView(dname);
    if (!classname) {
        throw new InvalidRouteError(params, 'no matching view');
    }
    let { ctxid, id } = evalIds();
    return {
        dname,
        cname,
        classname,
        tabname,
        ctxid,
        id,
        query: decodeTypes(query),
        authNeeded,
        isDefault
    };
};
export const decodeRequestWithAuth = (params, query, config)=>{
    let { authNeeded, ...res } = decodeRequest(params, query, config);
    if (!authNeeded) {
        return res;
    }
    let { dname, classname, tabname } = res;
    let classcfg = config.templating[dname].classes[classname];
    let aclDialog = 'xauth/loginDialog';
    // first
    if (classcfg.authNeeded) {
        aclDialog = get(classcfg, 'aclDialog', aclDialog);
    }
    if (tabname) {
        let tabcfg = classcfg.tabs[tabname];
        if (tabcfg.authNeeded) {
            aclDialog = get(tabcfg, 'aclDialog', aclDialog);
        }
    }
    let [dialogClass, dialogTab] = aclDialog.split('/');
    // store target router params, we will restore
    // original request in metaReducer/LOGIN_SUCCESS
    // TODO XXX: difference between login failure and not enough rights
    res.loginTarget = {
        params,
        query,
        reason: null
    };
    log.info(`login via ${aclDialog}`);
    // switch view
    res.classname = dialogClass;
    res.tabname = dialogTab;
    return res;
};
export const middleware = (store)=>(next)=>(action)=>{
            let result = next(action);
            if (action.type == 'ROUTER_POP') {
                let root = store.getState();
                // newly generated in restoreView reducer
                let { poppedRunId = 0, runId } = root.data;
                // check for cache miss
                if (poppedRunId > runId) {
                    let { pathname, search } = action.payload;
                    log.debug('run go() due ROUTER_POP and poppedRunId > runId');
                    store.dispatch(Actions.go(pathname + search, {
                        history: 'activate',
                        runId: poppedRunId
                    }));
                }
            }
            if (action.type == 'ROUTER_LOCATION_CHANGED') {
                /* this was done once purely in a templating/reducer activateView call
    but it's easier to reason about if we use a single activateView action
    */ let { payload } = action;
                let data = store.getState().data;
                let runId = get(payload.options, 'runId', data.poppedRunId > data.runId ? undefined : data.runId);
                if (runId > 0) {
                    store.dispatch(Actions.activateView(runId, payload));
                }
            }
            return result;
        };
